import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/app/global-components/button";
import { Form } from "@/app/global-components/form";
import {
  createTenderQuotationSchema,
  TenderQuotation,
} from "./tenderQuotationSchema";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import QuatationFieldView from "./quatation-field-view";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { isNull, isEmpty } from "lodash";
import CompanyWarningPopup from "@/app/features/CompanyCreate/companycreatePopup";
import * as XLSX from "xlsx";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { showToast } from "@/app/global-components/toaster";
import { routes } from "@/consts";
import useItems from "@/hooks/use-item";
import useOffer from "@/hooks/useTender";
import { LANG } from "@/types";
import { cn } from "@/lib/utils";

import { ImSpinner2 } from "react-icons/im";
import useRegisterCompany from "@/hooks/use-register-company";

interface Product {
  productId: string;
  unitName: string;
  SKUCode: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  notice: string;
  attachment?: File | null;
  image?: string | null | undefined;
}
interface Offer {
  title: string;
  minValue: number;
  value: number;
  endDate: string;
  deliverDate: string;
  type: string;
  status: string;
  categories: string[];
  region: string;
  city: string;
  fileName: string;
  fileDescription: string;
  attachmentName: string;
  attachmentDescription: string;
  receiveDocumentsType: string;
  contactInfo: string;
  products: ProductOffer[];
}
interface ProductOffer {
  item: string;
  quantity: number;
  attachment: File | null;
  notice: string;
}
const QuotationPage = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const tenderQuotationSchema = createTenderQuotationSchema(
    t as (key: string) => string
  );
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const { sendOffertRequest, getOffer } = useOffer();
  const { fetchItems } = useItems();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [offer, setOffer] = useState<Offer | null>(null);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const [products, setProducts] = useState<Product[]>([
    {
      productId: "",
      unitName: "",
      SKUCode: "",
      quantity: 1,
      unitPrice: 1,
      vat: 0,
      notice: "",
      attachment: null,
      image: null,
    },
  ]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [vat, setVat] = useState(0);
  const form = useForm<TenderQuotation>({
    resolver: zodResolver(tenderQuotationSchema),
    defaultValues: {
      id: null,
      tenderId: id,
      companyId: myCompany?.id,
      status: "Pending",
      products: [],
      deliverDays: 0,
      DeadLineDate: "7",
      paymentMethod: "1",
      contactMethod: "1",
      customDeliveryMethod: "",
      OpportunityNr: myCompany?.OpportunityNr + 1,
    },
  });
  const { getCompanyMine } = useRegisterCompany();
  const [isCompanyAvailable, setIsCompanyAvailable] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      await getCompanyMine().catch(() => {
        setIsCompanyAvailable(true);
      });
    };
    fetchCompany();
  }, []);
  // useEffect(() => {
  //   const userProfile = async () => {
  //     const res = await getUserProfile();
  //     dispatch(getMyCompany(res.data.company));
  //   };
  //   userProfile();
  // }, []);
  useEffect(() => {
    const loadOffer = async () => {
      try {
        if (id) {
          const offerData = await getOffer(id);
          if (offerData) {
            setOffer(offerData.data);
          }
        }
      } catch (error) {
        console.error("Error fetching offer:", error);
      }
    };

    loadOffer();
  }, [id]);

  useEffect(() => {
    const loadCompanyItems = async () => {
      try {
        if (myCompany?.id) {
          form.setValue("companyId", myCompany.id);
          form.setValue("OpportunityNr", myCompany.OpportunityNr + 1);
          const response = await fetchItems(myCompany.id);
          setItems(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };

    loadCompanyItems();
  }, [myCompany, form]);
  const onSubmit = async (values: TenderQuotation) => {
    try {
      setIsLoading(true);
      if (values.DeliveryMethod === "Other" && values.customDeliveryMethod) {
        values.DeliveryMethod = values.customDeliveryMethod;
      }
      await sendOffertRequest(values);
      showToast(t("Alert.SubmissionSuccess"), "warning");
      navigate(`${routes.vuetender}/${id}`);
    } catch (err) {
      setIsLoading(false);
      console.error("Error creating offer Quotation:", err);
    }
  };

  useEffect(() => {
    const products = form.getValues("products") ?? [];
    const price = products.reduce(
      (acc, product) =>
        acc +
        (product.unitPrice ?? 0) *
          (product.quantity ?? 0) *
          (1 + (product.vat ?? 0) / 100),
      0
    );
    const VAt = products.reduce(
      (acc, product) =>
        acc +
        (product.unitPrice ?? 0) *
          (product.quantity ?? 0) *
          ((product.vat ?? 0) / 100),
      0
    );
    setTotalPrice(price);
    setVat(VAt);
  }, [form.watch("products"), products]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setImportError(null); // Clear error when dialog is closed
  };
  // Handle Excel Import
  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (!data) return;

      const workbook = XLSX.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const importedData = XLSX.utils.sheet_to_json(sheet);

      const importedProducts = importedData.map((item: any) => ({
        productId: item.productId || "",
        unitName: item.unitName || "",
        SKUCode: item.SKUCode || "",
        quantity: Number(item.quantity) || 1,
        unitPrice: Number(item.unitPrice) || 0,
        vat: Number(item.vat) || 0,
        notice: item.notice || "",
        attachment: null,
        image: null,
      }));

      setProducts((prevProducts) => [...prevProducts, ...importedProducts]);
      form.setValue("products", [...products, ...importedProducts]);
    };

    reader.readAsBinaryString(file);
  };
  const handleDownloadTemplate = () => {
    const templateData = [
      ["unitName", "SKUCode", "quantity", "unitPrice", "vat", "notice"], // Headers
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

    XLSX.writeFile(workbook, "Product_Import_Template.xlsx");
  };

  const addProduct = () => {
    const newProduct: Product = {
      productId: "",
      unitName: "",
      SKUCode: "",
      quantity: 1,
      unitPrice: 1,
      vat: 0,
      notice: "",
      attachment: null,
      image: null,
    };
    const currentProducts = form.getValues("products") as Product[];
    const updatedProducts = [...currentProducts, newProduct];
    setProducts(updatedProducts);

    form.setValue("products", updatedProducts);
  };
  const removeProduct = (index: number) => {
    const currentProducts = form.getValues("products") as Product[];
    const newProducts = currentProducts.filter((_, i) => i !== index);
    setProducts(newProducts);
    form.setValue("products", newProducts);
  };

  const options = [
    { value: "7", label: `7 ${t("time.days")}` },
    { value: "14", label: `14 ${t("time.days")}` },
    { value: "30", label: `30 ${t("time.days")}` },
  ];

  // const optionsContact = [
  //   {
  //     value: "1",
  //     label: `${t("profile.fields.email")}`,
  //   },
  //   { value: "2", label: t("contactInfo.phone") },
  // ];
  const optionsDelievery = [
    { value: "FedEx", label: "FedEx" },
    { value: "DHL Express", label: "DHL Express" },
    { value: "SPL", label: "سُبل" },
    { value: "Other", label: t("delivery.other") },
  ];
  const columns = [
    {
      title: "",
      field: "image",
      render: (row: Product) =>
        row.image ? (
          <>
            <img src={row.image} alt="item" className="border mx-auto rounded-xl"
            style={{ width: 70, height: 70 }} />
          </>
        ) : (
          "—"
        ),
    },
    {
      title: t("checkout.fields.name"),
      field: "unitName",
      render: (row: Product) => {
        const index = products.findIndex(
          (product) => product.productId === row.productId
        );
        return (
          <QuatationFieldView
            name={`products.${index}.productId`}
            placeholder={t("offers.placeholders.EnterItemName")}
            label=""
            inputType="select"
            formControl={form.control}
            options={[
              ...items.map((item) => ({ value: item.id, label: item.name })),
            ]}
            onChange={(value) => {
              const selectedItem = items.find((item) => item.id === value);
              if (selectedItem) {
                const updatedProducts = [...products];
                updatedProducts[index] = {
                  ...updatedProducts[index],
                  productId: selectedItem.id,
                  unitName: selectedItem.name,
                  unitPrice: selectedItem.price,
                  SKUCode: selectedItem.SKUCode,
                  vat: selectedItem.vat,
                  image: selectedItem.image.url,
                };
                form.setValue("products", updatedProducts);
                setProducts(updatedProducts);
              }
            }}
          />
        );
      },
    },
    { title: "SKU", field: "productId" },
    {
      title: t("item.fields.vat"),
      field: "vat",
      render: (row: Product) => {
        const index = products.findIndex(
          (product) => product.productId === row.productId
        );
        return (
          <QuatationFieldView
            name={`products.${index}.vat`}
            placeholder={t("quatation.placeholders.EnterVAT")}
            label=""
            inputType="select"
            options={[
              { value: "0", label: "0%" },
              { value: "5", label: "5%" },
              { value: "15", label: "15%" },
            ]}
            formControl={form.control}
            onChange={(value) => {
              const updatedProducts = [...products];
              const parsedValue =
                typeof value === "string" ? value : value.target.value;
              updatedProducts[index].vat = parseFloat(parsedValue) || 0;
              setProducts(updatedProducts);
              form.setValue("products", updatedProducts);
            }}
          />
        );
      },
    },
    {
      title: t("checkout.fields.quantity"),
      field: "quantity",
      render: (row: Product) => {
        const index = products.findIndex(
          (product) => product.productId === row.productId
        );
        return (
          <QuatationFieldView
            name={`products.${index}.quantity`}
            placeholder={t("offers.placeholders.EnterQuantity")}
            label=""
            inputType="number"
            formControl={form.control}
            onChange={(value) => {
              const updatedProducts = [...products];
              const parsedValue =
                typeof value === "string" ? value : value.target.value;
              updatedProducts[index].quantity = parseInt(parsedValue, 10) || 0;
              setProducts(updatedProducts);
              form.setValue("products", updatedProducts);
            }}
          />
        );
      },
    },
    {
      title: t("checkout.fields.unitPrice"),
      field: "unitPrice",
      render: (row: Product) => {
        const index = products.findIndex(
          (product) => product.productId === row.productId
        );
        return (
          <QuatationFieldView
            name={`products.${index}.unitPrice`}
            placeholder={t("quatation.placeholders.EnterPrice")}
            label=""
            inputType="number"
            formControl={form.control}
            onChange={(value) => {
              const updatedProducts = [...products];
              const parsedValue =
                typeof value === "string" ? value : value.target.value;
              updatedProducts[index].unitPrice = parseFloat(parsedValue) || 0;
              setProducts(updatedProducts);
              form.setValue("products", updatedProducts);
            }}
          />
        );
      },
    },
    {
      title: t("orders.fields.notes"),
      field: "notice",
      render: (row: Product) => {
        const index = products.findIndex(
          (product) => product.productId === row.productId
        );
        return (
          <QuatationFieldView
            name={`products.${index}.notice`}
            placeholder={t("tender.fields.EnterNote")}
            label=""
            inputType="textarea"
            formControl={form.control}
          />
        );
      },
    },
    {
      title: t("attachments.title"),
      field: "attachment",
      render: (row: Product) => {
        const index = products.findIndex(
          (product) => product.productId === row.productId
        );
        return (
          <QuatationFieldView
            name={`products.${index}.attachment`}
            placeholder={t("offers.placeholders.SelectFile")}
            label=""
            inputType="file"
            formControl={form.control}
          />
        );
      },
    },
    {
      title: t("actions.Action"),
      field: "action",
      render: (row: Product) => {
        const index = products.findIndex(
          (product) => product.productId === row.productId
        );
        return (
          <Button color="secondary" onClick={() => removeProduct(index)}>
            <DeleteIcon />
          </Button>
        );
      },
    },
  ];
  const watchedProducts = form.watch("products") || [];
  return (
    <Box dir={locale === LANG.AR ? "rtl" : "ltr"}>
      {isCompanyAvailable && <CompanyWarningPopup open={true} />}
      <h6
        style={{
          textAlign: locale === LANG.AR ? "right" : "left",
          font: "inherit",
        }}
      >
        {t("company.fields.name")}
      </h6>
      <Typography
        variant="body1"
        align={locale === LANG.AR ? "right" : "left"}
        sx={{ mb: 3 }}
      >
        {myCompany?.CompanyNr}
      </Typography>

      <h6
        style={{
          textAlign: locale === LANG.AR ? "right" : "left",
          font: "inherit",
        }}
      >
        {t("products.list")}
      </h6>
      <div style={{ display: "flex", gap: "20px" }}>
        <Button
          className="flex items-center bg-secondary-brightYellow shadow text-textcolor-black  py-2 px-4 text-sm"
          style={{ background: "#000", color: "#fff" }}
          onClick={addProduct}
        >
          {t("products.add")}
        </Button>
        <Button
          className="flex items-center bg-secondary-brightYellow shadow text-textcolor-black  py-2 px-4 text-sm"
          style={{ background: "#000", color: "#fff" }}
          onClick={handleOpenDialog}
        >
          {t("actions.importExcel")}
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="overflow-y-scroll mt-4 no-scrollbar">
            {isNull(offer) || isEmpty(offer)
              ? ""
              : offer.products &&
                offer.products.length > 0 && (
                  <div className="my-6">
                    <h2 className="text-lg font-semibold">
                      {t("offers.items")}
                    </h2>
                    <table className="min-w-full table-auto">
                      <thead>
                        <tr>
                          <th
                            className={cn(
                              "border border-gray-300 p-2",
                              locale === LANG.AR ? "text-right" : "text-left"
                            )}
                          >
                            {t("offers.item")}
                          </th>
                          <th
                            className={cn(
                              "border border-gray-300 p-2",
                              locale === LANG.AR ? "text-right" : "text-left"
                            )}
                          >
                            {t("checkout.fields.quantity")}
                          </th>
                          <th
                            className={cn(
                              "border border-gray-300 p-2",
                              locale === LANG.AR ? "text-right" : "text-left"
                            )}
                          >
                            {t("offers.attachment")}
                          </th>
                          <th
                            className={cn(
                              "border border-gray-300 p-2",
                              locale === LANG.AR ? "text-right" : "text-left"
                            )}
                          >
                            {t("orders.fields.notes")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {offer.products.map((product, index) => (
                          <tr key={index}>
                            <td className="border px-4 py-2">{product.item}</td>
                            <td className="border px-4 py-2">
                              {product.quantity}
                            </td>
                            <td className="border px-4 py-2">
                              {product.attachment ? (
                                <a
                                  href={URL.createObjectURL(product.attachment)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t("attachments.view")}
                                </a>
                              ) : (
                                t("general.Unavailabe")
                              )}
                            </td>
                            <td className="border px-4 py-2">
                              {product.notice}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            <div
              className="bg-white py-4 rounded-xl mx-4 w-full relative"
              style={{ direction: locale === LANG.AR ? "rtl" : "ltr" }}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
                }}
              >
                {columns.map((column, index) => (
                  <div key={index} className="w-36 text-center font-bold">
                    {column.title}
                  </div>
                ))}
              </div>
              <div style={{ minHeight: 200, padding: "8px" }}>
                {watchedProducts.length > 0 ? (
                  watchedProducts.map((row, index) => (
                    <div
                      key={index}
                      className="grid gap-4 items-center cursor-pointer border-green-300 !rounded-2xl"
                      style={{
                        gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
                      }}
                    >
                      {columns.map((column, index) => {
                        return (
                          <div key={index} className="w-36 text-center">
                            {column.render
                              ? column.render(row)
                              : (row as any)[column.field]}
                          </div>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  <div className="text-center p-4">
                    {t("general.NoDataAvailable")}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* {t("orders.fields.totalPrice")} */}
          <h6
            className={cn(
              "text-lg font-semibold text-gray-800",
              locale == LANG.AR ? "text-right" : "text-left"
            )}
          >
            {t("item.fields.vat")}: {vat.toFixed(2)}{" "}
            {t("checkout.summary.currency")}
          </h6>
          <h6
            className={cn(
              "text-lg font-semibold text-gray-800",
              locale == LANG.AR ? "text-right" : "text-left"
            )}
          >
            {t("orders.fields.totalPrice")}: {totalPrice.toFixed(2)}{" "}
            {t("checkout.summary.currency")}
          </h6>

          <Grid container>
            <Grid item xs={12} lg={4}>
              <div style={{ maxWidth: "400px" }}>
                <QuatationFieldView
                  name="deliverDays"
                  placeholder=""
                  label={`${t("delivery.suggestedTime")} (${t("time.days")})`}
                  inputType="number"
                  formControl={form.control}
                />
                <QuatationFieldView
                  name="DeadLineDate"
                  placeholder=""
                  label={t("offers.validityPeriod1")}
                  inputType="select"
                  options={options}
                  formControl={form.control}
                />
              </div>
            </Grid>
            <Grid item xs={12} lg={4}>
              <QuatationFieldView
                name="DeliveryMethod"
                placeholder=""
                label={t("delivery.methods")}
                inputType="radio"
                options={optionsDelievery}
                formControl={form.control}
                onChange={(value) => {
                  if (value === "Other") {
                    form.setValue("customDeliveryMethod", "");
                  } else {
                    form.setValue("customDeliveryMethod", "");
                  }
                }}
              />
              {form.watch("DeliveryMethod") === "Other" && (
                <QuatationFieldView
                  name="customDeliveryMethod"
                  placeholder={t("delivery.enterMethod")}
                  label={t("delivery.customMethod")}
                  inputType="text"
                  formControl={form.control}
                />
              )}
            </Grid>
            {/* <Grid item xs={12} lg={4}>
              <QuatationFieldView
                name="contactMethod"
                placeholder=""
                label={t("contactInfo.title")}
                inputType="radio"
                options={optionsContact}
                formControl={form.control}
              />
            </Grid> */}
          </Grid>
          <Grid item xs={12} lg={12}>
            <FormControlLabel
              sx={{ margin: "5px auto" }}
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                />
              }
              label={t("general.AgreeToTerms")}
            />
          </Grid>
          <div className="flex mt-4 gap-4 justify-end">
            <Button
              variant="secondary"
              className="!border border-gray-400 bg-gray-300"
              onClick={handleGoBack}
            >
              {t("actions.cancel")}
            </Button>
            <Button
              className="flex items-center bg-secondary-brightYellow shadow text-textcolor-black py-2 px-4 text-sm"
              style={{ background: "#000", color: "#fff" }}
              type="submit"
              onClick={form.handleSubmit(onSubmit)}
              disabled={!acceptTerms || isLoading}
            >
              {t("actions.submit1")}
              {isLoading && <ImSpinner2 />}
            </Button>
          </div>
        </form>
      </Form>
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("actions.importExcel")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("general.UploadExcelMessage")}
          </Typography>
          <MuiButton
            variant="contained"
            component="label"
            color="secondary"
            sx={{ mb: 2 }}
          >
            {t("actions.uploadFile")}
            <input
              type="file"
              accept=".xlsx, .xls"
              hidden
              onChange={handleImportExcel}
            />
          </MuiButton>
          <MuiButton
            variant="outlined"
            color="secondary"
            onClick={handleDownloadTemplate}
            sx={{ mb: 2, mx: 2 }}
          >
            {t("actions.DownloadExcelTemplate")}
          </MuiButton>

          {importError && (
            <Typography variant="body2" color="error">
              {importError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={handleCloseDialog}
            className="!text-gray-200 !px-3 !bg-zinc-600"
          >
            {t("actions.close")}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuotationPage;
